import { sendContainerRequest } from '@/container'
import { functions, httpsCallable } from '@/firebase'
import { AUTHENTICATION_MODULE_NAME } from '@/store/moduleNames'

export const EMAIL_IS_ALREADY_USED_ERROR =
    'The sub account email already exists'

export const INVITATION_ALREADY_ACCEPTED_ERROR =
    'Invitation is already accepted'

const findRelationship = (subAccountUser, mainAccountId) => {
    const relationship = (subAccountUser.relationships ?? []).find(
        (relationship) => relationship.mainAccountId === mainAccountId
    )
    return relationship?.relationship
}

export const fetchSubAccounts = async (mainAccountId) => {
    const result = await sendContainerRequest('getSubAccountsByUser').then(
        (result) => result.data
    )
    if (result.success) {
        result.data.subAccounts.forEach((subAccount) => {
            subAccount.relationship = findRelationship(
                subAccount,
                mainAccountId
            )
        })
    }
    return result
}

export const createSubAccount = async (subAccountData) => {
    const { mainAccountId, fullName, dob, email, relationship } = subAccountData
    const body = {
        mainAccountId,
        subAccountName: fullName,
        subAccountRelationship: relationship,
    }
    if (email) {
        body.subAccountEmail = email
    }
    if (dob) {
        body.subAccountDob = dob
    }
    return sendContainerRequest('createSubAccountByUser', body).then(
        (result) => result.data
    )
}

export const editSubAccount = async (subAccountId, subAccountData) => {
    const { mainAccountId, fullName, dob, email, relationship } = subAccountData
    const body = {
        subAccountId,
        mainAccountId,
        fullName,
        relationship,
    }
    if (email) {
        body.newEmail = email
    }
    if (dob) {
        body.dob = dob
    }
    return sendContainerRequest('editSubAccountByUser', body).then(
        (result) => result.data
    )
}

export const sendSubAccountInvitation = async (subAccountData) => {
    const { mainAccountId, subAccountId, email, fullName, relationship } =
        subAccountData
    const body = {
        mainAccountId,
        subAccountEmail: email,
        subAccountFullName: fullName,
        relationship,
    }
    if (subAccountId) {
        body.oldSubAccountId = subAccountId
    }
    return sendContainerRequest('sendSubAccountInvitationByUser', body).then(
        (result) => result.data
    )
}

export const unlinkSubAccount = async (
    mainAccountId,
    subAccountId,
    newEmail = null
) => {
    const body = {
        mainAccountId,
        subAccountId,
    }
    if (newEmail) {
        body.newEmail = newEmail
    }
    return sendContainerRequest('unlinkSubAccountByUser', body).then(
        (result) => result.data
    )
}

export const getPackagesForSubAccount = async (
    mainAccountId,
    subAccountId,
    studioId
) => {
    return sendContainerRequest('getPackagesForSubAccount', {
        userId: mainAccountId,
        subAccountId,
        studioId,
    }).then((result) => result.data)
}

export const assignPackageToUser = async (packageId, subAccountId) => {
    return sendContainerRequest('assignPackageToUser', {
        packageId,
        assignedAccountId: subAccountId,
    }).then((result) => result.data)
}

export const assignClassSpotForFamily = async (
    classId,
    signupId,
    assignedFamilyId
) => {
    const body = {
        classId,
        signupId,
        assignedFamilyId,
        appSource: 'Booking Website',
    }
    return sendContainerRequest('assignClassSpot', body).then(
        (result) => result.data
    )
}
export const assignClassSpotForEmail = async (
    classId,
    signupId,
    assignedEmail
) => {
    const body = {
        classId,
        signupId,
        assignedEmail,
        appSource: 'Booking Website',
    }
    return sendContainerRequest('assignClassSpot', body).then(
        (result) => result.data
    )
}

export const getUserBookings = async (req) => {
    const { subAccountId, status, type, lastDocId, studioId } = req
    const body = {
        status, // 'upcoming' | 'waitlist' | 'past' | 'cancelled'
        type, // 'class' | 'course'
        studioId,
    }
    if (subAccountId) {
        body.subAccountId = subAccountId
    }
    if (lastDocId) {
        body.lastDocId = lastDocId
    }
    return sendContainerRequest('getUserBookings', body).then(
        (result) => result.data
    )
}

const getInvitationResponseType = (result) => {
    if (result.success) {
        return 'success'
    } else if (
        result.error &&
        result.error.includes(INVITATION_ALREADY_ACCEPTED_ERROR)
    ) {
        return 'accepted'
    }
    return 'invalid'
}

export const acceptSubAccountInvitation = async (token) => {
    return sendContainerRequest('acceptSubAccountInvitation', {
        token,
    }).then((result) => {
        const type = getInvitationResponseType(result.data)
        return {
            ...result.data,
            type,
        }
    })
}
